import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { PlayerRef } from "@remotion/player";
import type { VideoComposition } from "@hightouch/ad-studio-renderer";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useRegionConfig } from "src/utils/use-region-config";
import { resolvePreviewFrameSource } from "./preview-frame-origin";
import { useFontInjection } from "src/pages/ad-studio/hooks/use-font-injection";
import { preResolveAssets } from "./pre-resolve-assets";
import { PreviewIframeClient } from "./preview-iframe-client";
import {
  REMOTION_LOG_LEVEL_PARAM,
  remotionLogLevelFromSearch,
} from "src/ad-preview-frame/remotion-log-level";

export interface SandboxedCompositionPlayerSize {
  width: number;
  height: number;
}

interface SandboxedCompositionPlayerProps {
  composition: VideoComposition;
  componentAssets: Record<string, string> | null | undefined;
  style?: CSSProperties;
  playerRef?: React.RefObject<PlayerRef>;
  resolveAsset?: (ref: string) => string;
  onSizeChange?: (size: SandboxedCompositionPlayerSize) => void;
  /** Show built-in Remotion playback controls. Defaults to true. */
  controls?: boolean;
  /** Auto-play when mounted. */
  autoPlay?: boolean;
  /** Loop playback. */
  loop?: boolean;
  /** Start muted so autoplay works in all browsers. */
  initiallyMuted?: boolean;
  /** Allow clicking the player to play/pause. */
  clickToPlay?: boolean;
  /** Whether the spacebar toggles play/pause (Remotion's handler, inside the
   *  iframe). Set `false` when the host owns the spacebar (e.g. space-to-pan). */
  spaceKeyToPlayOrPause?: boolean;
  /**
   * Render nothing (instead of the opaque shimmer) while the composition and
   * its assets load. For embeds that composite over other content, e.g. the
   * video editor's above-scene still. Carried on the iframe URL because the
   * first shimmer paints before any postMessage arrives.
   */
  transparentLoading?: boolean;
}

/**
 * Renders the composition inside a sandboxed iframe so any agent-generated
 * custom-component JS executes in an opaque-origin context with no access
 * to the editor's cookies, localStorage, or DOM.
 *
 * Exposes the same external API as the inline `CompositionPlayer` — the
 * `playerRef` is assigned an object implementing the subset of `PlayerRef`
 * methods that the editor actually calls (getCurrentFrame, isPlaying, play,
 * pause, seekTo). Frame/playback state is cached from postMessage events.
 */
export function SandboxedCompositionPlayer({
  composition,
  componentAssets,
  style,
  playerRef,
  resolveAsset,
  onSizeChange,
  controls = true,
  autoPlay,
  loop,
  initiallyMuted,
  clickToPlay,
  spaceKeyToPlayOrPause,
  transparentLoading,
}: SandboxedCompositionPlayerProps) {
  const { apiEndpoint } = useRegionConfig();
  const { adStudioSeparateFrameOrigin } = useFlags();
  // Memoised for identity, not cost: the effect below depends on `frameSource`
  // and would tear down and rebuild the iframe client on every render.
  const frameSource = useMemo(
    () => resolvePreviewFrameSource(Boolean(adStudioSeparateFrameOrigin)),
    [adStudioSeparateFrameOrigin],
  );
  const { getAllFontRules } = useFontInjection();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const clientRef = useRef<PreviewIframeClient | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Create/destroy the iframe client and wire it to the parent's playerRef.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const client = new PreviewIframeClient(iframe, frameSource.origin);
    clientRef.current = client;

    if (playerRef) {
      // Map the `PlayerRef.addEventListener` API onto the iframe client's
      // event subscriptions. We only translate the events the editor
      // actually listens for; anything else logs a warning and no-ops so
      // we hear about gaps loudly.
      const listenerUnsubs = new Map<unknown, () => void>();
      const addEventListener = (event: string, callback: unknown): void => {
        const cb = callback as (arg: unknown) => void;
        let unsub: () => void;
        switch (event) {
          case "play":
            unsub = client.on("playStateChanged", (playing) => {
              if (playing) cb({});
            });
            break;
          case "pause":
            unsub = client.on("playStateChanged", (playing) => {
              if (!playing) cb({});
            });
            break;
          case "frameupdate":
            unsub = client.on("frameChanged", (frame) => {
              cb({ detail: { frame } });
            });
            break;
          case "mutechange":
            unsub = client.on("muteStateChanged", (isMuted) => {
              cb({ detail: { isMuted } });
            });
            break;
          default:
            console.warn(
              `[ad-preview] unsupported player event "${event}" — listener will not fire`,
            );
            unsub = () => {};
        }
        listenerUnsubs.set(callback, unsub);
      };
      const removeEventListener = (_event: string, callback: unknown): void => {
        const unsub = listenerUnsubs.get(callback);
        if (unsub) {
          unsub();
          listenerUnsubs.delete(callback);
        }
      };

      const handle = {
        getCurrentFrame: () => client.getCurrentFrame(),
        isPlaying: () => client.isPlaying(),
        isMuted: () => client.isMuted(),
        play: () => client.play(),
        pause: () => client.pause(),
        toggle: () => client.toggle(),
        mute: () => client.mute(),
        unmute: () => client.unmute(),
        seekTo: (frame: number) => client.seekTo(frame),
        addEventListener,
        removeEventListener,
      } as unknown as PlayerRef;
      (playerRef as React.MutableRefObject<PlayerRef | null>).current = handle;
    }

    return () => {
      client.destroy();
      clientRef.current = null;
      if (playerRef) {
        (playerRef as React.MutableRefObject<PlayerRef | null>).current = null;
      }
    };
  }, [playerRef, frameSource]);

  // Forward sizeChanged events to the optional onSizeChange callback.
  const onSizeChangeRef = useRef(onSizeChange);
  onSizeChangeRef.current = onSizeChange;
  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
    return client.on("sizeChanged", (size) => {
      onSizeChangeRef.current?.(size);
    });
  }, []);

  // Surface iframe errors. For Phase 2 we just log; a richer error UI lands later.
  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
    return client.on("error", (err) => {
      console.error("[ad-preview] iframe error", err);
    });
  }, []);

  // The sandboxed iframe swallows keyboard events, so re-dispatch the keys it
  // forwards on the parent document to drive page-level hotkeys.
  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
    return client.on("keydown", (key) => {
      for (const type of ["keydown", "keyup"] as const) {
        document.dispatchEvent(
          new KeyboardEvent(type, { key, code: key, bubbles: true }),
        );
      }
    });
  }, []);

  // Ship font rules on a separate channel from the composition. Fonts are
  // heavy (base64-inlined file fonts can be MB) and change rarely; sending
  // them inside every composition update was the largest per-update payload.
  // The iframe is opaque-origin: it can't use the parent's loaded faces or
  // fetch the brand-font URLs itself, so it relies entirely on these base64
  // rules. The composition send is gated on the same rules (see the effect
  // below) so the iframe never paints a fallback-font frame — it shows its
  // own shimmer until the composition arrives (LCM-80).
  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
    let canceled = false;
    // Inline all brand fonts (the iframe renders arbitrary layers, so we can't
    // scope to one markup string) and ship them once resolved. Cached by the
    // provider, so this is cheap on subsequent mounts.
    void getAllFontRules()
      .then((rules) => {
        if (canceled) return;
        client.setFontRules(rules);
      })
      .catch((err: Error) => {
        if (canceled) return;
        // The iframe falls back to system fonts. The composition send awaits
        // the same rules and surfaces its own error if it fails.
        console.error("[ad-preview] font ship failed", err);
      });
    return () => {
      canceled = true;
    };
  }, [getAllFontRules]);

  // Ship player-level options (controls/autoPlay/loop/initiallyMuted/
  // clickToPlay) on a separate channel — they're typically stable, and
  // bundling them with setComposition would mean re-sending on every
  // drag frame even though they almost never change.
  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
    client.setPlayerOptions({
      controls,
      autoPlay,
      loop,
      initiallyMuted,
      clickToPlay,
      spaceKeyToPlayOrPause,
    });
  }, [
    controls,
    autoPlay,
    loop,
    initiallyMuted,
    clickToPlay,
    spaceKeyToPlayOrPause,
  ]);

  // Pre-resolve assets and push the composition into the iframe whenever the
  // composition, componentAssets, customResolver, or apiEndpoint changes.
  useEffect(() => {
    let canceled = false;
    setResolveError(null);

    const loadComposition = async () => {
      const resolved = await preResolveAssets({
        composition,
        componentAssets,
        apiEndpoint,
        customResolver: resolveAsset,
      });
      if (canceled) return;

      // Gate the composition send on the font rules resolving. The font effect
      // ships them on its own channel — so we never re-send the heavy payload
      // on a composition change — and its `getAllFontRules()` runs first (it's
      // declared above and mounts earlier), so awaiting the same cached result
      // here guarantees `setFontRules` is queued ahead of `setComposition`. The
      // opaque-origin iframe therefore applies fonts before it ever paints
      // text, so no fallback-font frame renders. Cached, so this is a microtask
      // once warm (and a no-op when the brand has no fonts).
      await getAllFontRules();
      if (canceled) return;

      const client = clientRef.current;
      if (!client) return;
      client.setComposition(
        resolved.composition,
        resolved.componentAssets,
        resolved.resolvedRefs,
      );
    };

    loadComposition().catch((err: Error) => {
      if (canceled) return;
      console.error("[ad-preview] pre-resolution failed", err);
      setResolveError(err.message);
    });

    return () => {
      canceled = true;
    };
  }, [
    composition,
    componentAssets,
    resolveAsset,
    apiEndpoint,
    getAllFontRules,
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        ...style,
      }}
    >
      <iframe
        ref={iframeRef}
        src={previewFrameSrc(frameSource.src, transparentLoading)}
        // Safe only on the frame's own domain, which serves nothing
        // authenticated. See `PreviewFrameSource.origin` for the other case.
        sandbox={
          frameSource.origin
            ? "allow-scripts allow-same-origin"
            : "allow-scripts"
        }
        // The opaque-origin sandbox (no allow-same-origin) means user
        // activation from the parent's transport controls never crosses into
        // the iframe, so an `@ad-studio/audio` layer's Web Audio AudioContext
        // can't resume and audio playback throws NotAllowedError. Delegating
        // the autoplay permission lets the framed document start its
        // AudioContext without an in-frame gesture, without weakening the
        // sandbox. See https://developer.chrome.com/blog/autoplay/#iframe
        allow="autoplay"
        title="Ad preview"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
        }}
      />
      {resolveError && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
            padding: 16,
            fontSize: 14,
            fontFamily: "system-ui, sans-serif",
            textAlign: "center",
          }}
        >
          Failed to load preview: {resolveError}
        </div>
      )}
    </div>
  );
}

function previewFrameSrc(
  base: string,
  transparentLoading: boolean | undefined,
): string {
  const params = new URLSearchParams();
  if (transparentLoading) params.set("loading", "transparent");
  // Forwarded from the app page's URL, which is the one a person can edit.
  const logLevel = remotionLogLevelFromSearch(window.location.search);
  if (logLevel) params.set(REMOTION_LOG_LEVEL_PARAM, logLevel);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}
